import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { ApiService } from 'src/app/core/services/http/api.service'
import { LocalStorageService } from 'src/app/core/services/storage/local-storage.service'
import { StorageKeys } from 'src/app/shared/constants/storage-keys'
import { CompanyConfig } from 'src/app/shared/models/companyconfig'
import { environment } from 'src/environments/environment'

import { FileHashService } from './file-hash.service'

export type UploadPhase = 'hashing' | 'uploading' | 'registering' | 'done' | 'error' | 'stalled'

export interface UploadEvent {
  phase: UploadPhase
  pct: number
  loaded?: number
  total?: number
  media?: { id: string; path: string; mimeType: string; displayName: string; name: string; [key: string]: unknown }
  error?: string
  message?: string
}

export interface UploadOptions {
  type?: string
  // When true, the upload is not tracked in the global upload-progress overlay.
  // Used by flows that render their own progress UI (e.g. Read-from-Document),
  // so the user does not see two loaders for a single upload.
  silent?: boolean
}

export interface ActiveUpload {
  id: string
  fileName: string
  phase: UploadPhase
  pct: number
  error?: string
  message?: string
}

const STALL_WARNING_MS = 30_000
const API_BASE = `${environment.apiBase}/api/v2/`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UploadUrlResponse = any

@Injectable({ providedIn: 'root' })
export class UploadOrchestratorService {
  private readonly activeUploads = new Map<string, ActiveUpload>()
  private readonly activeUploads$ = new BehaviorSubject<ActiveUpload[]>([])
  private uploadCounter = 0
  // While > 0, every upload started is treated as silent (kept out of the global
  // upload-progress overlay). Used by import flows that render their own loader and
  // also trigger secondary uploads they don't control — e.g. Read-from-Document,
  // where the converted HTML's inline images are auto-uploaded by the editor and
  // would otherwise surface a second overlay on top of the read-text loader.
  private silentScopeDepth = 0

  constructor(
    private readonly api: ApiService,
    private readonly hashService: FileHashService,
    private readonly storage: LocalStorageService,
    private readonly http: HttpClient,
    private readonly translate: TranslateService
  ) {}

  getActiveUploads$(): Observable<ActiveUpload[]> {
    return this.activeUploads$.asObservable()
  }

  // Open/close a window during which all uploads are silent. Reference-counted so
  // nested or overlapping import flows don't end each other's scope prematurely.
  beginSilentScope(): void {
    this.silentScopeDepth++
  }

  endSilentScope(): void {
    if (this.silentScopeDepth > 0) this.silentScopeDepth--
  }

  dismissUpload(id: string): void {
    this.activeUploads.delete(id)
    this.emitActive()
  }

  dismissAll(): void {
    this.activeUploads.clear()
    this.emitActive()
  }

  private batchDismissTimer: ReturnType<typeof setTimeout> | null = null
  private readonly BATCH_DISMISS_DELAY_MS = 2500

  private emitActive(): void {
    this.activeUploads$.next(Array.from(this.activeUploads.values()))
    this.scheduleBatchDismissIfReady()
  }

  private upsertActive(entry: ActiveUpload): void {
    this.activeUploads.set(entry.id, entry)
    this.emitActive()
  }

  private scheduleBatchDismissIfReady(): void {
    if (this.batchDismissTimer) {
      clearTimeout(this.batchDismissTimer)
      this.batchDismissTimer = null
    }
    if (this.activeUploads.size === 0) return

    const entries = Array.from(this.activeUploads.values())
    const allTerminal = entries.every(e => e.phase === 'done' || e.phase === 'error')
    if (!allTerminal) return

    const anyError = entries.some(e => e.phase === 'error')
    if (anyError) return

    this.batchDismissTimer = setTimeout(() => {
      this.batchDismissTimer = null
      const stillAllDone =
        this.activeUploads.size > 0 && Array.from(this.activeUploads.values()).every(e => e.phase === 'done')
      if (stillAllDone) this.dismissAll()
    }, this.BATCH_DISMISS_DELAY_MS)
  }

  upload(file: File, options: UploadOptions = {}): Observable<UploadEvent> {
    const subject = new Subject<UploadEvent>()
    const id = `upload-${++this.uploadCounter}-${Date.now()}`
    const silent = options.silent === true || this.silentScopeDepth > 0
    if (!silent) this.upsertActive({ id, fileName: file.name, phase: 'hashing', pct: 0 })

    subject.subscribe({
      next: ev => {
        if (silent) return
        this.upsertActive({
          id,
          fileName: file.name,
          phase: ev.phase,
          pct: ev.pct,
          error: ev.error,
          message: ev.message
        })
      },
      error: err => {
        if (silent) return
        this.upsertActive({
          id,
          fileName: file.name,
          phase: 'error',
          pct: 0,
          error: (err as Error)?.message || 'upload failed'
        })
      }
    })

    // Defer run() into the next animation frame so the initial pct=0 state paints
    // before any progress event overwrites it. Without this, fast small-file uploads
    // can render only the final 100% state.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => void this.run(file, options, subject))
    } else {
      setTimeout(() => void this.run(file, options, subject), 0)
    }
    return subject.asObservable()
  }

  uploadAsMedia(formData: FormData, options: { silent?: boolean } = {}): Observable<UploadEvent['media']> {
    const file = formData.get('file')
    const type = (formData.get('type') as string | null) || undefined
    if (!(file instanceof File)) {
      return this.proxyAddMedia(formData)
    }
    return new Observable(observer => {
      const sub = this.upload(file, { type, silent: options.silent }).subscribe({
        next: ev => {
          if (ev.phase === 'done' && ev.media) {
            observer.next(ev.media)
            observer.complete()
          } else if (ev.phase === 'error') {
            observer.error(new Error(ev.error || this.translate.instant('FILEUPLOAD.ERROR.uploadFailed')))
          }
        },
        error: err => observer.error(err)
      })
      return () => sub.unsubscribe()
    })
  }

  private getMediaLimitBytes(): number | null {
    const company = this.storage.get(StorageKeys.companyProfile) as CompanyConfig | null
    const mb = company?.mediaLimit
    return typeof mb === 'number' && mb > 0 ? mb * 1024 * 1024 : null
  }

  private getUploadUrl(body: {
    filename: string
    mimeType: string
    size: number
    hash: string
    type?: string
  }): Observable<UploadUrlResponse> {
    return this.api.sendRequest({
      apiBase: API_BASE,
      method: 'post',
      endpoint: 'media/get-upload-url',
      body
    })
  }

  private registerUpload(body: { key: string }): Observable<UploadEvent['media']> {
    return this.api.sendRequest({
      apiBase: API_BASE,
      method: 'post',
      endpoint: 'media/register-upload',
      body
    })
  }

  private abortUpload(body: { key: string }): Observable<{ ok: boolean }> {
    return this.api.sendRequest({
      apiBase: API_BASE,
      method: 'post',
      endpoint: 'media/abort-upload',
      body
    })
  }

  private proxyAddMedia(body: FormData): Observable<UploadEvent['media']> {
    return this.api.sendRequest({
      apiBase: API_BASE,
      method: 'post',
      endpoint: 'media/upload-file',
      body
    })
  }

  private proxyAddMediaWithProgress(
    body: FormData,
    onProgress: (loaded: number, total: number) => void
  ): Observable<UploadEvent['media']> {
    return new Observable(observer => {
      const req = new HttpRequest('POST', `${API_BASE}media/upload-file`, body, {
        reportProgress: true
      })
      const sub = this.http.request(req).subscribe({
        next: (event: { type: number; loaded?: number; total?: number; body?: unknown }) => {
          if (event.type === HttpEventType.UploadProgress) {
            if (event.total) onProgress(event.loaded || 0, event.total)
          } else if (event.type === HttpEventType.Response) {
            observer.next(event.body as UploadEvent['media'])
            observer.complete()
          }
        },
        error: err => observer.error(err)
      })
      return () => sub.unsubscribe()
    })
  }

  private async animateProgress(
    subject: Subject<UploadEvent>,
    phase: UploadPhase,
    total: number,
    durationMs = 600
  ): Promise<void> {
    const steps = 10
    const stepMs = durationMs / steps
    for (let i = 1; i <= steps; i++) {
      const pct = (i / steps) * 100
      subject.next({ phase, pct, loaded: Math.round((i / steps) * total), total })
      await new Promise<void>(resolve => setTimeout(resolve, stepMs))
    }
  }

  private async run(file: File, options: UploadOptions, subject: Subject<UploadEvent>): Promise<void> {
    try {
      const limit = this.getMediaLimitBytes()
      if (limit !== null && file.size > limit) {
        const limitMb = Math.round(limit / (1024 * 1024))
        throw new Error(this.translate.instant('FILEUPLOAD.ERROR.fileExceedsLimit', { mb: limitMb }))
      }

      subject.next({ phase: 'hashing', pct: 0 })
      const hash = await this.hashService.hash(file, p =>
        subject.next({ phase: 'hashing', pct: p.pct, loaded: p.loaded, total: p.total })
      )

      const urlResp = await this.getUploadUrl({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        hash,
        type: options.type
      }).toPromise()

      if (!urlResp || urlResp.useProxy === true) {
        subject.next({ phase: 'uploading', pct: 0, loaded: 0, total: file.size })
        const formData = new FormData()
        formData.append('file', file)
        if (options.type) formData.append('type', options.type)
        const media = await this.proxyAddMediaWithProgress(formData, (loaded, total) => {
          subject.next({
            phase: 'uploading',
            pct: (loaded / total) * 100,
            loaded,
            total
          })
        }).toPromise()
        subject.next({ phase: 'done', pct: 100, media })
        subject.complete()
        return
      }

      if ('media' in urlResp) {
        subject.next({ phase: 'uploading', pct: 0, loaded: 0, total: file.size })
        await this.animateProgress(subject, 'uploading', file.size, 600)
        subject.next({ phase: 'done', pct: 100, media: urlResp.media })
        subject.complete()
        return
      }

      subject.next({ phase: 'uploading', pct: 0, loaded: 0, total: file.size })
      await this.uploadToS3(file, urlResp, subject)

      subject.next({ phase: 'registering', pct: 100 })
      const created = await this.registerUpload({
        key: urlResp.key
      }).toPromise()

      subject.next({ phase: 'done', pct: 100, media: created })
      subject.complete()
    } catch (err) {
      subject.next({
        phase: 'error',
        pct: 0,
        error: (err as Error)?.message || this.translate.instant('FILEUPLOAD.ERROR.uploadFailed')
      })
      subject.error(err)
    }
  }

  private uploadToS3(
    file: File,
    presigned: { url: string; fields: Record<string, string>; key: string },
    subject: Subject<UploadEvent>
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData()
      Object.entries(presigned.fields).forEach(([name, value]) => formData.append(name, value))
      formData.append('file', file)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', presigned.url, true)

      let lastProgressAt = Date.now()
      let stalledNotified = false
      const stallTimer = setInterval(() => {
        if (Date.now() - lastProgressAt > STALL_WARNING_MS && !stalledNotified) {
          stalledNotified = true
          subject.next({
            phase: 'stalled',
            pct: 0,
            message: this.translate.instant('FILEUPLOAD.STALL.warning')
          })
        }
      }, 5000)

      const cleanup = () => clearInterval(stallTimer)

      xhr.upload.onprogress = e => {
        lastProgressAt = Date.now()
        stalledNotified = false
        if (!e.lengthComputable) return
        subject.next({
          phase: 'uploading',
          pct: (e.loaded / e.total) * 100,
          loaded: e.loaded,
          total: e.total
        })
      }
      xhr.onload = () => {
        cleanup()
        if (xhr.status >= 200 && xhr.status < 300) resolve()
        else
          reject(
            new Error(
              this.translate.instant('FILEUPLOAD.ERROR.s3UploadFailed', {
                status: xhr.status,
                statusText: xhr.statusText
              })
            )
          )
      }
      xhr.onerror = () => {
        cleanup()
        this.fireAbort(presigned)
        reject(new Error(this.translate.instant('FILEUPLOAD.ERROR.s3NetworkError')))
      }
      xhr.onabort = () => {
        cleanup()
        this.fireAbort(presigned)
        reject(new Error(this.translate.instant('FILEUPLOAD.ERROR.s3Aborted')))
      }
      xhr.send(formData)
    })
  }

  private fireAbort(presigned: { key: string }): void {
    this.abortUpload({ key: presigned.key }).subscribe({
      error: () => undefined
    })
  }
}
